import { Stack } from '@mui/material'
import FatnessCard from 'components/cattles/fatnessPage/ui/FatnessCard'
import FatnessList from 'components/cattles/fatnessPage/ui/FatnessList'
import {
	GROUP_STATUS_TABS,
	mapGroupStatusToTab,
} from 'components/groups/constants/groupStatuses'
import { useGetGroups } from 'hook/query/useGetGroupsList'
import { useGetTabsForGroups } from 'hook/query/useGetTabsForGroups'
import { useMultiSelect } from 'hook/useMultiSelect'
import PageContainer from 'layouts/PageContainer'
import { GroupsStatusTabs } from 'models/IGroup'
import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import BreadCrumbsGroup from 'ui/breadCrumbs/BreadCrumbsGroup'

const FatnessPage = () => {
	const [searchParams] = useSearchParams()
	const groupFromQuery = searchParams.get('group')
	const initialGroups = useMemo(() => {
		const groupId = Number(groupFromQuery)
		return Number.isFinite(groupId) && groupId > 0 ? [groupId] : []
	}, [groupFromQuery])

	const groupFilters = useMultiSelect<number>(initialGroups)
	const statusFilters = useMultiSelect<GroupsStatusTabs>([])

	const { data: tabsData } = useGetTabsForGroups()
	const { data: groupsList = [] } = useGetGroups({})

	const visibleStatusTabs = useMemo(() => {
		if (!tabsData) return []

		return GROUP_STATUS_TABS.filter(tab => tabsData[tab.key] != null)
	}, [tabsData])

	const showStatusTabs = visibleStatusTabs.length > 1

	const groupsWithStatus = useMemo(
		() =>
			groupsList.map(group => ({
				...group,
				statusTab: mapGroupStatusToTab(group.status),
			})),
		[groupsList],
	)

	const availableGroupIds = useMemo(() => {
		if (statusFilters.selected.length === 0) return null

		return new Set(
			groupsWithStatus
				.filter(
					group =>
						group.statusTab && statusFilters.selected.includes(group.statusTab),
				)
				.map(group => group.id),
		)
	}, [groupsWithStatus, statusFilters.selected])

	const availableStatuses = useMemo(() => {
		if (groupFilters.selected.length === 0) return null

		return new Set(
			groupsWithStatus
				.filter(group => groupFilters.selected.includes(group.id))
				.map(group => group.statusTab)
				.filter(Boolean) as GroupsStatusTabs[],
		)
	}, [groupsWithStatus, groupFilters.selected])

	useEffect(() => {
		if (!availableGroupIds) return

		groupFilters.setSelected(prev => {
			const next = prev.filter(id => availableGroupIds.has(id))
			return next.length === prev.length ? prev : next
		})
	}, [availableGroupIds, groupFilters.setSelected])

	const effectiveGroupIds = useMemo(() => {
		if (groupFilters.selected.length > 0) {
			return groupFilters.selected
		}

		if (availableGroupIds) {
			return Array.from(availableGroupIds)
		}

		return []
	}, [groupFilters.selected, availableGroupIds])

	const statusBreadcrumbs = useMemo(
		() =>
			visibleStatusTabs.map(tab => ({
				id: tab.key,
				title: tab.title,
				selected: statusFilters.selected.includes(tab.key),
				disabled: availableStatuses !== null && !availableStatuses.has(tab.key),
			})),
		[visibleStatusTabs, statusFilters.selected, availableStatuses],
	)

	const groupBreadcrumbs = useMemo(
		() =>
			groupsWithStatus.map(group => ({
				id: group.id,
				title: group.title,
				selected: groupFilters.selected.includes(group.id),
				disabled:
					!groupFilters.selected.includes(group.id) &&
					availableGroupIds !== null &&
					!availableGroupIds.has(group.id),
			})),
		[groupsWithStatus, groupFilters.selected, availableGroupIds],
	)

	return (
		<PageContainer>
			<Stack direction='column' gap='12px'>
				{showStatusTabs && (
					<BreadCrumbsGroup
						handleActiveFilter={statusFilters.toggle}
						handleRemoveFilter={statusFilters.remove}
						array={statusBreadcrumbs}
					/>
				)}
				<BreadCrumbsGroup
					handleActiveFilter={groupFilters.toggle}
					handleRemoveFilter={groupFilters.remove}
					array={groupBreadcrumbs}
				/>
			</Stack>
			<FatnessCard activeFilter={effectiveGroupIds} />
			<FatnessList activeFilter={effectiveGroupIds} />
		</PageContainer>
	)
}

export default FatnessPage
